import { useState } from 'react'
import AssignmentStage from './AssignmentStage'
import TeamsTable from './TeamsTable'
import TeamName from './TeamName'
import Wheel from './Wheel'
import { TEAM_COLORS } from '../../event/colors'
import type { AdminEvent } from './useAdminEvent'

type Phase = 'idle' | 'first' | 'second' | 'paired' | 'color' | 'saving'

const big =
  'rounded-lg border-2 border-white bg-[var(--color-accent-600)] px-6 py-3 text-lg font-black text-white hover:bg-[var(--color-accent-700)] disabled:opacity-50'
const quiet =
  'rounded-md border border-[var(--page-rule)] px-3.5 py-2 text-sm font-semibold hover:bg-[var(--page-canvas-sunken)] disabled:opacity-50'

/**
 * The team reveal, for the projector. Teams are formed and locked on the
 * server first; the wheel then reveals them one at a time: two spins for the
 * pair, then a color wheel for the team name. Each team is saved once its
 * color lands, so that team's phones update.
 */
function FormationStage({ admin }: { admin: AdminEvent }) {
  const { state, busy, act } = admin
  const [phase, setPhase] = useState<Phase>('idle')
  const [wheelNames, setWheelNames] = useState<string[]>([])
  const [wheelFills, setWheelFills] = useState<string[] | undefined>(undefined)
  const [target, setTarget] = useState<string | null>(null)
  const [spin, setSpin] = useState(0)
  const [landed, setLanded] = useState<string[]>([])
  const [announcement, setAnnouncement] = useState('')

  if (!state) return null

  const byId = new Map(state.roster.map((p) => [p.id, p]))
  const nameOf = (id: string) => byId.get(id)?.name ?? id
  const missionFor = (teamId: string) => state.missions.find((m) => m.id === state.assignments[teamId]) ?? null
  const present = state.roster.filter((p) => p.present).length
  const current = state.teams[state.revealedCount] ?? null
  // Wheel slices are tracked by roster id, so two people sharing a name stay distinct.
  const remaining = state.teams.slice(state.revealedCount).flatMap((t) => t.memberIds)
  const done = state.teams.length > 0 && state.revealedCount >= state.teams.length

  function startTeam() {
    if (!current) return
    const [first] = current.memberIds
    setLanded([])
    setAnnouncement('')
    setWheelFills(undefined)
    setWheelNames(remaining)
    setTarget(first)
    setPhase('first')
    setSpin((n) => n + 1)
  }

  async function onLanded() {
    if (!current) return
    const [first, second] = current.memberIds
    if (phase === 'first') {
      setLanded([first])
      setAnnouncement(`${nameOf(first)}.`)
      // A beat on the first name, then the wheel spins again without them.
      setTimeout(() => {
        setWheelNames(remaining.filter((n) => n !== first))
        setTarget(second)
        setPhase('second')
        setSpin((n) => n + 1)
      }, 1400)
      return
    }
    if (phase === 'second') {
      setLanded([first, second])
      setAnnouncement(`${nameOf(first)} and ${nameOf(second)}.`)
      setPhase('paired')
      return
    }
    if (phase === 'color') {
      setPhase('saving')
      const saved = await act({ type: 'revealNextTeam' })
      setAnnouncement(saved ? `Team ${current.name}: ${nameOf(first)} and ${nameOf(second)}.` : '')
      setPhase('idle')
    }
  }

  // Colors still in play: every web color not already shown, plus this
  // team's own, which the wheel lands on.
  function spinColor() {
    if (!current) return
    const shownColors = new Set(state!.teams.slice(0, state!.revealedCount).map((t) => t.name))
    const choices = TEAM_COLORS.filter((c) => !shownColors.has(c.name) || c.name === current.name)
    if (!choices.some((c) => c.name === current.name)) choices.push({ name: current.name, hex: current.color })
    setWheelNames(choices.map((c) => c.name))
    setWheelFills(choices.map((c) => c.hex))
    setTarget(current.name)
    setPhase('color')
    setSpin((n) => n + 1)
  }

  if (state.teams.length === 0) {
    return (
      <section aria-labelledby="team-stage" className="flex flex-col items-start gap-4 rounded-xl bg-[var(--color-royal)] px-6 py-10 text-white">
        <h2 id="team-stage" className="wordmark-display-fluid">
          Team formation
        </h2>
        <p className="text-2xl font-bold">{present} builders ready to pair.</p>
        <button type="button" className={big} disabled={busy} onClick={() => act({ type: 'generate' }, 'Teams formed.')}>
          Form teams
        </button>
      </section>
    )
  }

  if (!state.locked) {
    return (
      <section aria-labelledby="team-stage" className="flex flex-col items-start gap-4 rounded-xl bg-[var(--color-royal)] px-6 py-10 text-white">
        <h2 id="team-stage" className="wordmark-display-fluid">
          {state.teams.length} teams formed
        </h2>
        <p className="text-xl">The pairs are set and hidden. Lock them to start the wheel.</p>
        <div className="flex flex-wrap gap-3">
          <button type="button" className={big} disabled={busy} onClick={() => act({ type: 'lock' }, 'Teams locked. Spin away.')}>
            Lock teams and start the reveal
          </button>
          <button
            type="button"
            className="rounded-lg border-2 border-white px-5 py-3 font-bold text-white hover:bg-white/10 disabled:opacity-50"
            disabled={busy}
            onClick={() => act({ type: 'generate' }, 'Teams formed again.')}
          >
            Re-pair
          </button>
        </div>
      </section>
    )
  }

  const spinning = phase !== 'idle'
  const shown = state.teams.slice(0, state.revealedCount)
  const lastShown = shown[shown.length - 1]

  return (
    <section aria-labelledby="team-stage" className="flex flex-col gap-8">
      <p className="sr-only" role="status">
        {announcement}
      </p>

      <div className="grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div>
          {done ? (
            <div className="rounded-xl bg-[var(--color-royal)] px-6 py-10 text-white">
              <h2 id="team-stage" className="wordmark-display-fluid">
                All {state.teams.length} teams are set
              </h2>
              <p className="mt-2 text-xl">Next up: team assignments.</p>
            </div>
          ) : (
            <Wheel
              names={spinning ? wheelNames : remaining}
              labels={spinning && wheelFills ? undefined : (spinning ? wheelNames : remaining).map(nameOf)}
              fills={spinning ? wheelFills : undefined}
              target={target}
              spin={spin}
              onLanded={onLanded}
            />
          )}
        </div>

        <div className="flex flex-col gap-4">
          {!done && (
            <h2 id="team-stage" className="wordmark-display-fluid">
              {!spinning && lastShown ? (
                <TeamName name={lastShown.name} color={lastShown.color} />
              ) : (
                `Team ${state.revealedCount + 1}`
              )}
            </h2>
          )}
          <div className="flex min-h-36 flex-col gap-3">
            {(spinning ? landed : lastShown ? lastShown.memberIds : []).map((id) => (
              <p key={id} className="rounded-lg border-2 border-[var(--color-royal)] px-5 py-4 text-3xl font-black">
                {nameOf(id)}
              </p>
            ))}
            {!spinning && lastShown && missionFor(lastShown.id) && (
              <p className="text-lg">
                <span className="font-bold">Mission:</span> {missionFor(lastShown.id)!.mission}
              </p>
            )}
          </div>
          {!done &&
            (phase === 'paired' ? (
              <button type="button" className={`${big} self-start`} disabled={busy} onClick={spinColor}>
                Spin for the team color
              </button>
            ) : (
              <button type="button" className={`${big} self-start`} disabled={busy || spinning} onClick={startTeam}>
                {spinning ? 'Spinning' : `Spin for team ${state.revealedCount + 1}`}
              </button>
            ))}
        </div>
      </div>

      <TeamsTable state={state} />

      <div>
        <button type="button" className={quiet} disabled={busy || spinning} onClick={() => act({ type: 'unlock' }, 'Teams unlocked. The reveal starts over.')}>
          Unlock and start over
        </button>
      </div>
    </section>
  )
}

type Step = 'formation' | 'assignments'

/**
 * The Teams tab: team formation on the wheel, then team assignments on the
 * slot machine. Assignments open once every team has been revealed.
 */
export default function TeamStage({ admin }: { admin: AdminEvent }) {
  const formed = Boolean(admin.state && admin.state.teams.length > 0 && admin.state.revealedCount >= admin.state.teams.length)
  const [chosen, setChosen] = useState<Step | null>(null)
  const step: Step = chosen ?? (formed ? 'assignments' : 'formation')

  const steps: { key: Step; label: string; enabled: boolean }[] = [
    { key: 'formation', label: '1. Team formation', enabled: true },
    { key: 'assignments', label: '2. Team assignments', enabled: formed },
  ]

  return (
    <div className="flex flex-col gap-6">
      <div role="tablist" aria-label="Team steps" className="flex flex-wrap gap-2">
        {steps.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={step === s.key}
            disabled={!s.enabled}
            onClick={() => setChosen(s.key)}
            className={`cursor-pointer rounded-full border-2 px-4 py-2 text-sm font-black disabled:cursor-default disabled:opacity-50 ${
              step === s.key
                ? 'border-[var(--color-royal)] bg-[var(--color-royal)] text-white'
                : 'border-[var(--page-rule)] text-[var(--page-ink-muted)]'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {step === 'assignments' && formed ? <AssignmentStage admin={admin} /> : <FormationStage admin={admin} />}
    </div>
  )
}
